import React, { useState, useMemo, useEffect } from "react";
import ReactDOM from "react-dom";
import { Drawer, Button } from "antd";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import CompRowCard from "../../MarketSearch/components/CompRowCard";
import CompetitiveMarketProfile from "../../MarketSearch/components/CompetitiveMarketProfile";
import "../../MarketSearch/components/activeCompsTab.css";
import "../../MarketSearch/components/compRowCard.css";
import "../../MarketSearch/components/MobileFiltersDrawer.css";
import "./ViewInsightsDrawer.css";

function formatMileageRange([min, max]) {
  const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(0)}k` : String(n));
  return `${fmt(min)} — ${fmt(max)} mi`;
}

const MOBILE_BREAKPOINT = 767;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return isMobile;
}

// Helper function to check if image is valid
function isValidImageUrl(imgUrl) {
  if (!imgUrl || imgUrl.trim() === '') return false;
  // Check for common placeholder/watermark patterns
  const invalidPatterns = [
    'placeholder',
    'watermark',
    'via.placeholder',
    'no-image',
    'noimage',
    'default-image',
    'missing-image'
  ];
  return !invalidPatterns.some(pattern => imgUrl.toLowerCase().includes(pattern));
}

// Helper component for image with fallback
function ImageThumbnail({ imageUrl, alt }) {
  const [hasError, setHasError] = useState(false);
  
  if (!isValidImageUrl(imageUrl) || hasError) {
    return (
      <div className="vid-crc-no-image">
        <span>No image Available</span>
      </div>
    );
  }
  
  return (
    <img 
      src={imageUrl} 
      alt={alt}
      onError={() => setHasError(true)}
    />
  );
}

export default function ViewInsightsDrawer({ open, onClose, signal }) {
  const isMobile = useIsMobile();
  const [overviewPeriod, setOverviewPeriod] = useState("90D");
  const [soldCompsPeriod, setSoldCompsPeriod] = useState("90D");
  const [chartTab, setChartTab] = useState("active");
  const [activeTab, setActiveTab] = useState("overview");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileSoldFiltersOpen, setMobileSoldFiltersOpen] = useState(false);
  
  const MILEAGE_VARIANCES = [2, 5, 10, 15, 20];
  const RADIUS_OPTIONS_ACTIVE = [25, 50, 100, 250, 500];
  const RADIUS_OPTIONS_SOLD = [25, 50, 100];

  // Filter state for Active Comps
  const [activeCompsSortBy, setActiveCompsSortBy] = useState("price");
  const [activeCompsSortOrder, setActiveCompsSortOrder] = useState("asc");
  const [activeCompsMileageRange, setActiveCompsMileageRange] = useState([0, 200000]);
  const [activeCompsSimilarMileage, setActiveCompsSimilarMileage] = useState(false);
  const [activeCompsSimilarMileagePct, setActiveCompsSimilarMileagePct] = useState(15);
  const [activeCompsIndependentOnly, setActiveCompsIndependentOnly] = useState(false);
  const [activeCompsMatchingVersion, setActiveCompsMatchingVersion] = useState(false);
  const [activeCompsRadius, setActiveCompsRadius] = useState(100);
  
  // Filter state for Sold Comps
  const [soldCompsSortBy, setSoldCompsSortBy] = useState("soldDate");
  const [soldCompsSortOrder, setSoldCompsSortOrder] = useState("desc");
  const [soldCompsMileageRange, setSoldCompsMileageRange] = useState([0, 200000]);
  const [soldCompsSimilarMileage, setSoldCompsSimilarMileage] = useState(false);
  const [soldCompsSimilarMileagePct, setSoldCompsSimilarMileagePct] = useState(15);
  const [soldCompsIndependentOnly, setSoldCompsIndependentOnly] = useState(false);
  const [soldCompsMatchingVersion, setSoldCompsMatchingVersion] = useState(false);
  const [soldCompsRadius, setSoldCompsRadius] = useState(100);

  // Calculate mileage range for Active Comps
  const activeCompsMileageBounds = useMemo(() => {
    if (!signal || !signal.active_listings || signal.active_listings.length === 0) {
      return { min: 0, max: 200000 };
    }
    const mileages = signal.active_listings
      .map(listing => listing.miles || listing.mileage || 0)
      .filter(m => m > 0);
    if (mileages.length === 0) {
      return { min: 0, max: 200000 };
    }
    const min = Math.floor(Math.min(...mileages));
    const max = Math.ceil(Math.max(...mileages));
    return { min, max };
  }, [signal]);

  // Calculate mileage range for Sold Comps (filtered by Sold Comps tab period)
  const soldCompsMileageBounds = useMemo(() => {
    if (!signal || !signal.sold_listings || signal.sold_listings.length === 0) {
      return { min: 0, max: 200000 };
    }
    const filterByPeriod = (dateString, period) => {
      if (!dateString) return false;
      const now = new Date();
      let daysAgo = 30;
      if (period === "60D") daysAgo = 60;
      else if (period === "90D") daysAgo = 90;
      const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
      const date = new Date(dateString);
      return date >= cutoffDate;
    };
    const filteredSold = signal.sold_listings.filter(sale => filterByPeriod(sale.last_seen_at_date, soldCompsPeriod));
    const mileages = filteredSold
      .map(listing => listing.miles || listing.mileage || 0)
      .filter(m => m > 0);
    if (mileages.length === 0) {
      return { min: 0, max: 200000 };
    }
    const min = Math.floor(Math.min(...mileages));
    const max = Math.ceil(Math.max(...mileages));
    return { min, max };
  }, [signal, soldCompsPeriod]);

  // Handle ESC key to close drawer
  useEffect(() => {
    if (!open) return;
    
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  // Reset filters when tab changes
  useEffect(() => {
    // Reset Active Comps filters with dynamic range
    setActiveCompsSortBy("price");
    setActiveCompsSortOrder("asc");
    setActiveCompsMileageRange([activeCompsMileageBounds.min, activeCompsMileageBounds.max]);
    setActiveCompsSimilarMileage(false);
    setActiveCompsIndependentOnly(false);
    setActiveCompsMatchingVersion(false);
    
    // Reset Sold Comps filters with dynamic range
    setSoldCompsSortBy("soldDate");
    setSoldCompsSortOrder("desc");
    setSoldCompsMileageRange([soldCompsMileageBounds.min, soldCompsMileageBounds.max]);
    setSoldCompsSimilarMileage(false);
    setSoldCompsIndependentOnly(false);
    setSoldCompsMatchingVersion(false);
  }, [activeTab, activeCompsMileageBounds.min, activeCompsMileageBounds.max, soldCompsMileageBounds.min, soldCompsMileageBounds.max]);

  // Reset filters and tab only when viewing a different listing (signal ID changes).
  // Do NOT depend on soldCompsMileageBounds/activeCompsMileageBounds, or changing the period
  // (e.g. 90D → 60D on Sold Comps) would retrigger this and switch the tab back to Overview.
  useEffect(() => {
    if (!signal) return;
    
    // Reset to overview tab and default periods
    setActiveTab("overview");
    setOverviewPeriod("90D");
    setSoldCompsPeriod("90D");
    setChartTab("active");
    
    // Reset Active Comps filters with dynamic range
    setActiveCompsSortBy("price");
    setActiveCompsSortOrder("asc");
    setActiveCompsMileageRange([activeCompsMileageBounds.min, activeCompsMileageBounds.max]);
    setActiveCompsSimilarMileage(false);
    setActiveCompsIndependentOnly(false);
    setActiveCompsMatchingVersion(false);
    setActiveCompsRadius(100);
    
    // Reset Sold Comps filters with dynamic range
    setSoldCompsSortBy("soldDate");
    setSoldCompsSortOrder("desc");
    setSoldCompsMileageRange([soldCompsMileageBounds.min, soldCompsMileageBounds.max]);
    setSoldCompsSimilarMileage(false);
    setSoldCompsIndependentOnly(false);
    setSoldCompsMatchingVersion(false);
    setSoldCompsRadius(100);
  }, [signal?.id]); // Only run when listing changes; bounds omitted so changing period on Sold Comps doesn't switch tab

  // Calculate metrics based on Overview tab period
  const metrics = useMemo(() => {
    if (!signal) {
      return { activeCount: 0, soldCount: 0, avgDOM: 0, marketDaysSupply: null };
    }
    
    // Units Active: prefer API response (active_count / units_active), fall back to active_listings length
    const activeCount = signal.activeCount ?? signal.active_listings?.length ?? 0;
    
    // Filter sold listings by Overview tab time period
    const filterByPeriod = (dateString, period) => {
      if (!dateString) return false;
      const now = new Date();
      let daysAgo = 30;
      if (period === "60D") daysAgo = 60;
      else if (period === "90D") daysAgo = 90;
      const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
      const date = new Date(dateString);
      return date >= cutoffDate;
    };
    
    const soldListings = signal.sold_listings || [];
    const filteredSold = soldListings.filter(sale => filterByPeriod(sale.last_seen_at_date, overviewPeriod));
    const soldCount = filteredSold.length;
    
    // Days in period for MDS
    const daysInPeriod = overviewPeriod === "30D" ? 30 : overviewPeriod === "60D" ? 60 : 90;
    
    // Market Days Supply: activeCount / (soldCount / daysInPeriod) = activeCount * daysInPeriod / soldCount
    const marketDaysSupply = soldCount > 0
      ? Math.round((activeCount * daysInPeriod) / soldCount)
      : null;
    
    // Calculate average DOM from filtered sold listings
    let avgDOM = 0;
    if (filteredSold.length > 0) {
      const domValues = filteredSold
        .map(sale => sale.dom || sale.dom_active || 0)
        .filter(dom => dom > 0);
      if (domValues.length > 0) {
        const sum = domValues.reduce((acc, dom) => acc + dom, 0);
        avgDOM = Math.round(sum / domValues.length);
      }
    }
    
    return {
      activeCount,
      soldCount,
      avgDOM,
      marketDaysSupply,
    };
  }, [signal, overviewPeriod]);

  // Prepare scatter chart data (Overview tab: uses overviewPeriod for sold data)
  const scatterData = useMemo(() => {
    if (!signal) {
      return { active: [], sold: [] };
    }
    const activeListings = signal.active_listings || [];
    const soldListings = signal.sold_listings || [];

    const filterByPeriod = (dateString, period) => {
      if (!dateString) return false;
      const now = new Date();
      let daysAgo = 30;
      if (period === "60D") daysAgo = 60;
      else if (period === "90D") daysAgo = 90;
      const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
      const date = new Date(dateString);
      return date >= cutoffDate;
    };

    // Active listings should always show all, regardless of time period
    const activeData = activeListings
      .map(listing => ({
        price: listing.price || 0,
        mileage: listing.miles || 0,
      }));

    // Sold listings filtered by Overview tab period
    const soldData = soldListings
      .filter(sale => filterByPeriod(sale.last_seen_at_date, overviewPeriod))
      .map(sale => ({
        price: sale.price || 0,
        mileage: sale.miles || 0,
      }));

    return {
      active: activeData,
      sold: soldData,
    };
  }, [signal, overviewPeriod]);

  // Calculate median prices
  const medianActive = useMemo(() => {
    if (!signal) return 0;
    if (scatterData.active.length > 0) {
      const prices = scatterData.active.map(d => d.price).filter(p => p > 0).sort((a, b) => a - b);
      if (prices.length > 0) {
        const mid = Math.floor(prices.length / 2);
        return prices.length % 2 === 0
          ? Math.round((prices[mid - 1] + prices[mid]) / 2)
          : prices[mid];
      }
    }
    return signal.price || 16700;
  }, [scatterData.active, signal]);

  const medianSold = useMemo(() => {
    if (!signal) return 0;
    if (scatterData.sold.length > 0) {
      const prices = scatterData.sold.map(d => d.price).filter(p => p > 0).sort((a, b) => a - b);
      if (prices.length > 0) {
        const mid = Math.floor(prices.length / 2);
        return prices.length % 2 === 0
          ? Math.round((prices[mid - 1] + prices[mid]) / 2)
          : prices[mid];
      }
    }
    return signal.retail || 15400;
  }, [scatterData.sold, signal]);

  // Prepare comps data for Active and Sold sections
  const activeComps = useMemo(() => {
    if (!signal) return [];
    const listings = signal.active_listings || [];
    const subjectMileage = signal.mileage || 0;
    const subjectVersion = signal.version;
    
    // Map listings to comp format (distance from API: distance_miles, distance_mi, or distance)
    let comps = listings.map((listing) => ({
      id: listing.vin || `active-${listing.vin}`,
      price: listing.price || 0,
      mileage: listing.miles || 0,
      dom: listing.dom_active ?? listing.dom ?? 0,
      mileageDiff: Math.abs((listing.miles || 0) - subjectMileage),
      vin: listing.vin,
      dealer: listing.dealer_name || "Unknown",
      dealer_type: listing.dealer_type,
      version: listing.version,
      url: listing.vdp_url,
      heading: listing.heading || `${signal.year} ${signal.make} ${signal.model}`,
      image: listing.photo || signal.thumbnailUrl || null,
      distance_miles: listing.distance_miles ?? listing.distance_mi ?? listing.distance ?? null,
    }));
    
    // Apply radius filter: keep listings within selected radius (or with no distance data)
    comps = comps.filter((c) => c.distance_miles == null || c.distance_miles <= activeCompsRadius);
    
    // Apply mileage filter (use selected similar-mileage percentage)
    if (activeCompsSimilarMileage && subjectMileage) {
      const pct = activeCompsSimilarMileagePct / 100;
      const minMileage = subjectMileage * (1 - pct);
      const maxMileage = subjectMileage * (1 + pct);
      comps = comps.filter(comp => comp.mileage >= minMileage && comp.mileage <= maxMileage);
    } else {
      const [min, max] = activeCompsMileageRange;
      comps = comps.filter(comp => comp.mileage >= min && comp.mileage <= max);
    }
    
    // Independent dealers only filter
    if (activeCompsIndependentOnly) {
      comps = comps.filter(comp => comp.dealer_type === "independent");
    }
    
    // Matching version only filter
    if (activeCompsMatchingVersion && subjectVersion) {
      comps = comps.filter(comp => comp.version === subjectVersion);
    }
    
    // Sort
    comps.sort((a, b) => {
      let aVal, bVal;
      switch (activeCompsSortBy) {
        case "price":
          aVal = a.price;
          bVal = b.price;
          break;
        case "mileage":
          aVal = a.mileage;
          bVal = b.mileage;
          break;
        default:
          return 0;
      }
      return activeCompsSortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });
    
    return comps;
  }, [signal, activeCompsSortBy, activeCompsSortOrder, activeCompsMileageRange, activeCompsSimilarMileage, activeCompsSimilarMileagePct, activeCompsIndependentOnly, activeCompsMatchingVersion, activeCompsRadius]);
  
  // Calculate summary stats for Active Comps
  const activeCompsSummaryStats = useMemo(() => {
    if (activeComps.length === 0) {
      return { activeListings: 0, avgPrice: 0, avgMileage: 0 };
    }
    const totalPrice = activeComps.reduce((sum, comp) => sum + comp.price, 0);
    const totalMileage = activeComps.reduce((sum, comp) => sum + comp.mileage, 0);
    return {
      activeListings: activeComps.length,
      avgPrice: Math.round(totalPrice / activeComps.length),
      avgMileage: Math.round(totalMileage / activeComps.length),
    };
  }, [activeComps]);

  const soldComps = useMemo(() => {
    if (!signal) return [];
    const listings = signal.sold_listings || [];
    const subjectMileage = signal.mileage || 0;
    const subjectVersion = signal.version;

    const filterByPeriod = (dateString, period) => {
      if (!dateString) return false;
      const now = new Date();
      let daysAgo = 30;
      if (period === "60D") daysAgo = 60;
      else if (period === "90D") daysAgo = 90;
      const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
      const date = new Date(dateString);
      return date >= cutoffDate;
    };
    
    // Map listings to comp format (filtered by Sold Comps tab period; distance from API: distance_miles, distance_mi, or distance)
    let comps = listings
      .filter(sale => filterByPeriod(sale.last_seen_at_date, soldCompsPeriod))
      .map((listing) => ({
        id: listing.vin || `sold-${listing.vin}`,
        price: listing.price || 0,
        mileage: listing.miles || 0,
        dom: listing.dom || listing.dom_active || 0,
        mileageDiff: Math.abs((listing.miles || 0) - subjectMileage),
        vin: listing.vin,
        dealer: listing.dealer_name || "Unknown",
        dealer_type: listing.dealer_type,
        version: listing.version,
        url: listing.vdp_url,
        soldDate: listing.last_seen_at_date,
        lastSeenAtDate: listing.last_seen_at_date,
        heading: listing.heading || `${signal.year} ${signal.make} ${signal.model}`,
        image: listing.photo || signal.thumbnailUrl || null,
        distance_miles: listing.distance_miles ?? listing.distance_mi ?? listing.distance ?? null,
      }));
    
    // Apply radius filter: keep listings within selected radius (or with no distance data)
    comps = comps.filter((c) => c.distance_miles == null || c.distance_miles <= soldCompsRadius);
    
    // Apply mileage filter
    if (soldCompsSimilarMileage && subjectMileage) {
      const pct = soldCompsSimilarMileagePct / 100;
      const minMileage = subjectMileage * (1 - pct);
      const maxMileage = subjectMileage * (1 + pct);
      comps = comps.filter(comp => comp.mileage >= minMileage && comp.mileage <= maxMileage);
    } else {
      const [min, max] = soldCompsMileageRange;
      comps = comps.filter(comp => comp.mileage >= min && comp.mileage <= max);
    }
    
    // Independent dealers only filter
    if (soldCompsIndependentOnly) {
      comps = comps.filter(comp => comp.dealer_type === "independent");
    }
    
    // Matching version only filter
    if (soldCompsMatchingVersion && subjectVersion) {
      comps = comps.filter(comp => comp.version === subjectVersion);
    }
    
    // Sort
    comps.sort((a, b) => {
      if (soldCompsSortBy === "soldDate") {
        if (!a.lastSeenAtDate && !b.lastSeenAtDate) return 0;
        if (!a.lastSeenAtDate) return 1;
        if (!b.lastSeenAtDate) return -1;
        const aDate = new Date(a.lastSeenAtDate).getTime();
        const bDate = new Date(b.lastSeenAtDate).getTime();
        if (isNaN(aDate) && isNaN(bDate)) return 0;
        if (isNaN(aDate)) return 1;
        if (isNaN(bDate)) return -1;
        return soldCompsSortOrder === "asc" ? aDate - bDate : bDate - aDate;
      } else if (soldCompsSortBy === "price") {
        const aVal = a.price;
        const bVal = b.price;
        return soldCompsSortOrder === "asc" ? aVal - bVal : bVal - aVal;
      } else if (soldCompsSortBy === "mileage") {
        const aVal = a.mileage;
        const bVal = b.mileage;
        return soldCompsSortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }
      return 0;
    });
    
    return comps;
  }, [signal, soldCompsPeriod, soldCompsSortBy, soldCompsSortOrder, soldCompsMileageRange, soldCompsSimilarMileage, soldCompsSimilarMileagePct, soldCompsIndependentOnly, soldCompsMatchingVersion, soldCompsRadius]);

  // Calculate summary stats for Sold Comps
  const soldCompsSummaryStats = useMemo(() => {
    if (!soldComps || soldComps.length === 0) {
      return { soldListings: 0, avgPrice: 0, avgMileage: 0, avgSoldDom: 0 };
    }
    const totalPrice = soldComps.reduce((sum, comp) => sum + (comp.price || 0), 0);
    const totalMileage = soldComps.reduce((sum, comp) => sum + (comp.mileage || 0), 0);
    const totalDom = soldComps.reduce((sum, comp) => sum + (comp.dom || 0), 0);
    return {
      soldListings: soldComps.length,
      avgPrice: Math.round(totalPrice / soldComps.length),
      avgMileage: Math.round(totalMileage / soldComps.length),
      avgSoldDom: Math.round(totalDom / soldComps.length),
    };
  }, [soldComps]);

  // Adapter: build Market Search–shaped data for CompetitiveMarketProfile (Appraisal Tool)
  const appraisalData = useMemo(() => {
    if (!signal) return null;
    return {
      vehicle_info: {
        year: signal.year,
        make: signal.make,
        model: signal.model,
        trim: signal.trim,
        version: signal.version,
        mileage: signal.mileage,
      },
      search_metadata: {
        mileage: signal.mileage,
        asking_price: signal.price,
        version: signal.version,
      },
      active_listings: signal.active_listings || [],
      market_analysis: {
        active_listings: signal.active_listings || [],
        recent_sales: signal.sold_listings || [],
        sold_listings: signal.sold_listings || [],
      },
      pricing_info: {
        marketcheck_price: signal.retail ?? signal.price,
        hooptie_price: signal.retail ?? signal.price,
      },
    };
  }, [signal]);

  // Early return after all hooks
  if (!signal) return null;

  const priceCompression = medianActive - medianSold;
  const estimatedProfit = signal.profitPotential || (signal.retail - signal.price) || 4300;

  // Get status badge text
  const getStatusBadge = () => {
    const tags = signal.tags || [];
    if (tags.length > 1) {
      return tags.map(tag => tag.toUpperCase()).join(" • ");
    }
    return tags[0] ? tags[0].toUpperCase() : "FAST MOVER";
  };

  const statusBadge = getStatusBadge();

  // Price vs Mileage Scatter Chart Component (like Market Search)
  const PriceMileageChart = ({ data, chartTab, hooptiePrice, hooptieMileage }) => {
    const currentData = chartTab === "active" ? data.active : data.sold;

    if (currentData.length === 0) {
      return (
        <div className="vid-price-mileage-chart">
          <div className="vid-chart-tabs">
            <button
              className={`vid-chart-tab ${chartTab === "active" ? "active" : ""}`}
              onClick={() => setChartTab("active")}
            >
              Active
            </button>
            <button
              className={`vid-chart-tab ${chartTab === "sold" ? "active" : ""}`}
              onClick={() => setChartTab("sold")}
            >
              Sold
            </button>
          </div>
          <div className="vid-scatter-no-data">No data available for the selected time period.</div>
        </div>
      );
    }

    const hooptieVehicle = { price: hooptiePrice, mileage: hooptieMileage };
    const allPrices = [...currentData.map(d => d.price), hooptieVehicle.price];
    const allMileages = [...currentData.map(d => d.mileage), hooptieVehicle.mileage];
    
    const minPrice = Math.min(...allPrices) - 500;
    const maxPrice = Math.max(...allPrices) + 500;
    const minMileage = Math.min(...allMileages) - 5000;
    const maxMileage = Math.max(...allMileages) + 5000;

    const chartWidth = 600;
    const chartHeight = 400;
    const padding = { top: 20, right: 20, bottom: 40, left: 60 };

    const scaleX = (mileage) => {
      return ((mileage - minMileage) / (maxMileage - minMileage)) * (chartWidth - padding.left - padding.right) + padding.left;
    };

    const scaleY = (price) => {
      return chartHeight - padding.bottom - ((price - minPrice) / (maxPrice - minPrice)) * (chartHeight - padding.top - padding.bottom);
    };

    // Generate axis labels
    const priceSteps = 5;
    const priceStep = (maxPrice - minPrice) / priceSteps;
    const priceLabels = [];
    for (let i = 0; i <= priceSteps; i++) {
      priceLabels.push(minPrice + priceStep * i);
    }

    const mileageSteps = 5;
    const mileageStep = (maxMileage - minMileage) / mileageSteps;
    const mileageLabels = [];
    for (let i = 0; i <= mileageSteps; i++) {
      mileageLabels.push(minMileage + mileageStep * i);
    }

    return (
      <div className="vid-price-mileage-chart">
        <div className="vid-chart-tabs">
          <button
            className={`vid-chart-tab ${chartTab === "active" ? "active" : ""}`}
            onClick={() => setChartTab("active")}
          >
            Active
          </button>
          <button
            className={`vid-chart-tab ${chartTab === "sold" ? "active" : ""}`}
            onClick={() => setChartTab("sold")}
          >
            Sold
          </button>
        </div>

        <div className="vid-scatter-chart-container">
          <svg className="vid-scatter-svg" viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
            {/* Grid lines */}
            {priceLabels.map((price, i) => (
              <line
                key={`price-grid-${i}`}
                x1={padding.left}
                y1={scaleY(price)}
                x2={chartWidth - padding.right}
                y2={scaleY(price)}
                stroke="rgba(255, 255, 255, 0.05)"
                strokeWidth="1"
              />
            ))}
            {mileageLabels.map((mileage, i) => (
              <line
                key={`mileage-grid-${i}`}
                x1={scaleX(mileage)}
                y1={padding.top}
                x2={scaleX(mileage)}
                y2={chartHeight - padding.bottom}
                stroke="rgba(255, 255, 255, 0.05)"
                strokeWidth="1"
              />
            ))}

            {/* Y-axis labels (Price) */}
            {priceLabels.map((price, i) => (
              <text
                key={`price-label-${i}`}
                x={padding.left - 10}
                y={scaleY(price) + 4}
                fill="rgba(255, 255, 255, 0.4)"
                fontSize="10"
                textAnchor="end"
              >
                ${(price / 1000).toFixed(1)}k
              </text>
            ))}

            {/* X-axis labels (Mileage) */}
            {mileageLabels.map((mileage, i) => (
              <text
                key={`mileage-label-${i}`}
                x={scaleX(mileage)}
                y={chartHeight - padding.bottom + 20}
                fill="rgba(255, 255, 255, 0.4)"
                fontSize="10"
                textAnchor="middle"
              >
                {(mileage / 1000).toFixed(0)}k
              </text>
            ))}

            {/* Axis labels */}
            <text
              x={chartWidth / 2}
              y={chartHeight - 5}
              fill="rgba(255, 255, 255, 0.6)"
              fontSize="11"
              textAnchor="middle"
              fontWeight="600"
            >
              Mileage
            </text>
            <text
              x={15}
              y={chartHeight / 2}
              fill="rgba(255, 255, 255, 0.6)"
              fontSize="11"
              textAnchor="middle"
              fontWeight="600"
              transform={`rotate(-90, 15, ${chartHeight / 2})`}
            >
              Price
            </text>

            {/* Data points */}
            {currentData.map((point, index) => {
              const fillColor = chartTab === "active" ? "rgba(96, 165, 250, 0.6)" : "rgba(251, 191, 36, 0.6)";
              const strokeColor = chartTab === "active" ? "rgba(96, 165, 250, 0.9)" : "rgba(251, 191, 36, 0.9)";
              return (
                <circle
                  key={`point-${index}`}
                  cx={scaleX(point.mileage)}
                  cy={scaleY(point.price)}
                  r="4"
                  fill={fillColor}
                  stroke={strokeColor}
                  strokeWidth="1"
                />
              );
            })}

            {/* Hooptie vehicle (main dot) */}
            <circle
              cx={scaleX(hooptieVehicle.mileage)}
              cy={scaleY(hooptieVehicle.price)}
              r="8"
              fill="rgba(83, 223, 155, 0.8)"
              stroke="rgba(83, 223, 155, 1)"
              strokeWidth="2"
              className="vid-hooptie-dot"
            />
            <circle
              cx={scaleX(hooptieVehicle.mileage)}
              cy={scaleY(hooptieVehicle.price)}
              r="12"
              fill="none"
              stroke="rgba(83, 223, 155, 0.3)"
              strokeWidth="1"
              className="vid-hooptie-dot-ring"
            />
          </svg>
        </div>

        <div className="vid-scatter-legend">
          <div className="vid-legend-item">
            <span 
              className="vid-legend-dot vid-legend-data" 
              style={{
                background: chartTab === "active" ? "rgba(96, 165, 250, 0.6)" : "rgba(251, 191, 36, 0.6)",
                borderColor: chartTab === "active" ? "rgba(96, 165, 250, 0.9)" : "rgba(251, 191, 36, 0.9)"
              }}
            ></span>
            <span>{chartTab === "active" ? "Active" : "Sold"} Market</span>
          </div>
          <div className="vid-legend-item">
            <span className="vid-legend-dot vid-legend-hooptie"></span>
            <span>FB Listing</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Drawer
      title={null}
      placement="right"
      onClose={onClose}
      open={open}
      width={isMobile ? "100%" : 720}
      closable={false}
      className={`vid-drawer ${isMobile ? "vid-drawer--mobile" : ""}`}
      styles={{
        header: {
          display: "none",
        },
        body: {
          padding: 0,
          background: "#0d0f1a",
          overflowX: "hidden",
        },
        mask: { backdropFilter: "blur(2px)" },
        content: {
          borderRadius: 0,
          boxShadow: "0 25px 50px rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <div className="vid-wrapper">
        {/* Top Title */}
        <div className="vid-top-title">
          <div className="vid-title-content">
            <div className="vid-title-text">
              {signal?.year} {signal?.make} {signal?.model}{signal?.trim ? ` ${signal.trim}` : ''}{signal?.version ? ` • ${signal.version}` : ''}
            </div>
            <div className="vid-title-meta">
              <span className="vid-meta-item">Mileage: {(signal?.miles || signal?.mileage)?.toLocaleString() || 'N/A'}</span>
              {(signal?.listing_date || signal?.listedAt) && (
                <>
                  <span className="vid-meta-separator"> • </span>
                  <span className="vid-meta-item">Listed: {new Date(signal.listing_date || signal.listedAt).toLocaleString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                </>
              )}
            </div>
          </div>
          <button className="vid-close-btn" onClick={onClose}>
            <CloseOutlined />
          </button>
        </div>

        {/* Header Section */}
        <div className="vid-header">
          <div className="vid-header-content">
            <div className="vid-price-bar-section">
              {(() => {
                const isRecommended = signal?.recommended === true || signal?.recommended === "true";
                const askingPrice = signal?.price || signal?.asking_price || 0;
                const retailPrice = signal?.retail || 0;
                const profit = signal?.profit || signal?.profitPotential || estimatedProfit || 0;
                const totalPrice = askingPrice + profit; // End of the bar (asking + profit)
                const range = totalPrice - askingPrice || 1;
                // When not recommended: asking only, centered; no retail/profit
                const retailPosition = isRecommended && retailPrice > askingPrice
                  ? ((retailPrice - askingPrice) / range) * 100
                  : 0;
                const askingMarkerLeft = isRecommended ? '0%' : '50%';
                return (
                  <div className={`vid-price-bar-container ${!isRecommended ? 'vid-price-bar-container-ask-only' : ''}`}>
                    <div className="vid-price-bar">
                      <div className="vid-price-bar-gradient"></div>
                      <div 
                        className={`vid-price-marker vid-price-marker-asking ${!isRecommended ? 'vid-price-marker-asking-center' : ''}`}
                        style={{ left: askingMarkerLeft }}
                      ></div>
                      {isRecommended && (
                        <>
                          <div 
                            className="vid-price-marker vid-price-marker-retail"
                            style={{ left: `${Math.min(100, Math.max(0, retailPosition))}%` }}
                          ></div>
                          <div 
                            className="vid-price-marker vid-price-marker-profit"
                            style={{ left: '100%' }}
                          ></div>
                        </>
                      )}
                    </div>
                    <div className="vid-price-labels">
                      <div className="vid-price-label-item">
                        <div className="vid-price-label-text">Asking</div>
                        <div className="vid-price-label-value">${askingPrice.toLocaleString()}</div>
                      </div>
                      {isRecommended && (
                        <>
                          <div className="vid-price-label-item">
                            <div className="vid-price-label-text">Retail</div>
                            <div className="vid-price-label-value">${retailPrice.toLocaleString()}</div>
                          </div>
                          <div className="vid-price-label-item">
                            <div className="vid-price-label-text">Profit</div>
                            <div className="vid-price-label-value vid-price-label-value-profit">${profit.toLocaleString()}</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Main Tabs */}
        <div className="vid-main-tabs">
          <button
            className={`vid-main-tab ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          <button
            className={`vid-main-tab ${activeTab === "active" ? "active" : ""}`}
            onClick={() => setActiveTab("active")}
          >
            Active
          </button>
          <button
            className={`vid-main-tab ${activeTab === "sold" ? "active" : ""}`}
            onClick={() => setActiveTab("sold")}
          >
            Sold
          </button>
          <button
            className={`vid-main-tab ${activeTab === "appraisal" ? "active" : ""}`}
            onClick={() => setActiveTab("appraisal")}
          >
            Appraisal
          </button>
        </div>

        {/* Time Period Selector - Overview tab (independent from Sold Comps) */}
        {activeTab === "overview" && (
          <div className="vid-time-period">
            <span className="vid-period-label">Time Period:</span>
            <div className="vid-period-buttons">
              <button
                className={`vid-period-btn ${overviewPeriod === "30D" ? "active" : ""}`}
                onClick={() => setOverviewPeriod("30D")}
              >
                30D
              </button>
              <button
                className={`vid-period-btn ${overviewPeriod === "60D" ? "active" : ""}`}
                onClick={() => setOverviewPeriod("60D")}
              >
                60D
              </button>
              <button
                className={`vid-period-btn ${overviewPeriod === "90D" ? "active" : ""}`}
                onClick={() => setOverviewPeriod("90D")}
              >
                90D
              </button>
            </div>
          </div>
        )}

        {/* Time Period Selector - Sold Comps tab (independent from Overview) */}
        {activeTab === "sold" && (
          <div className="vid-time-period">
            <span className="vid-period-label">Time Period:</span>
            <div className="vid-period-buttons">
              <button
                className={`vid-period-btn ${soldCompsPeriod === "30D" ? "active" : ""}`}
                onClick={() => setSoldCompsPeriod("30D")}
              >
                30D
              </button>
              <button
                className={`vid-period-btn ${soldCompsPeriod === "60D" ? "active" : ""}`}
                onClick={() => setSoldCompsPeriod("60D")}
              >
                60D
              </button>
              <button
                className={`vid-period-btn ${soldCompsPeriod === "90D" ? "active" : ""}`}
                onClick={() => setSoldCompsPeriod("90D")}
              >
                90D
              </button>
            </div>
          </div>
        )}

        {/* Scrollable Content */}
        <div className="vid-content">
          {activeTab === "appraisal" && appraisalData && (
            <div className="vid-appraisal-wrap">
              <CompetitiveMarketProfile data={appraisalData} mobile={isMobile} hideHooptiePriceAndSoldDom />
            </div>
          )}
          {activeTab === "overview" && (
            <>
          {/* Market Metrics */}
          <div className="vid-metrics-grid">
            <div className="vid-metric-card">
              <div className="vid-metric-label">UNITS ACTIVE</div>
              <div className="vid-metric-value">{metrics.activeCount}</div>
            </div>
            <div className="vid-metric-card">
              <div className="vid-metric-label">SOLD ({overviewPeriod})</div>
              <div className="vid-metric-value">{metrics.soldCount}</div>
            </div>
            <div className="vid-metric-card">
              <div className="vid-metric-label">AVG DOM</div>
              <div className="vid-metric-value">{metrics.avgDOM} Days</div>
            </div>
            <div className="vid-metric-card">
              <div className="vid-metric-label">MDS</div>
              <div className="vid-metric-value">
                {metrics.marketDaysSupply != null ? `${metrics.marketDaysSupply} Days` : "—"}
              </div>
            </div>
          </div>

          {/* Price vs Mileage Chart */}
          <div className="vid-comps-chart-section">
            <div className="vid-comps-chart-title">Price vs Mileage</div>
            <PriceMileageChart
              data={scatterData}
              chartTab={chartTab}
              hooptiePrice={signal?.retail || signal?.price || 15000}
              hooptieMileage={signal?.mileage || 70000}
            />
          </div>
            </>
          )}

          {activeTab === "active" && isMobile ? (
            <>
              <div className="active-comps-tab active-comps-tab-mobile">
                <div className="act-mobile-pills">
                  <button type="button" className="act-mobile-pill act-mobile-pill-filters" onClick={() => setMobileFiltersOpen(true)}>
                    Filters {[activeCompsSimilarMileage, activeCompsIndependentOnly, activeCompsMatchingVersion].filter(Boolean).length > 0 ? `(${[activeCompsSimilarMileage, activeCompsIndependentOnly, activeCompsMatchingVersion].filter(Boolean).length})` : ""}
                  </button>
                  <button type="button" className="act-mobile-pill act-mobile-pill-sort" onClick={() => setActiveCompsSortOrder(activeCompsSortOrder === "asc" ? "desc" : "asc")}>
                    Sort: {activeCompsSortBy === "price" ? "Price" : "Mileage"} {activeCompsSortOrder === "asc" ? "↑" : "↓"}
                  </button>
                </div>
                {mobileFiltersOpen && ReactDOM.createPortal(
                  <div className="mfd-overlay" onClick={() => setMobileFiltersOpen(false)}>
                    <div className="mfd-drawer" onClick={(e) => e.stopPropagation()}>
                      <header className="mfd-header">
                        <button type="button" className="mfd-close" onClick={() => setMobileFiltersOpen(false)} aria-label="Close">
                          <CloseOutlined />
                        </button>
                        <h2 className="mfd-title">Filters</h2>
                        <button type="button" className="mfd-clear" onClick={() => { setActiveCompsSortBy("price"); setActiveCompsSortOrder("asc"); setActiveCompsMileageRange([activeCompsMileageBounds.min, activeCompsMileageBounds.max]); setActiveCompsSimilarMileage(false); setActiveCompsIndependentOnly(false); setActiveCompsMatchingVersion(false); setActiveCompsRadius(100); }}>
                          Clear
                        </button>
                      </header>
                      <div className="mfd-body">
                        <div className="mfd-divider" />
                        <section className="mfd-section">
                          <h3 className="mfd-section-title">MARKET SCOPE</h3>
                          <div className="mfd-block">
                            <label className="mfd-field-label">Radius</label>
                            <select
                              className="mfd-select"
                              value={activeCompsRadius}
                              onChange={(e) => setActiveCompsRadius(Number(e.target.value))}
                              aria-label="Search radius in miles"
                            >
                              {RADIUS_OPTIONS_ACTIVE.map((mi) => (
                                <option key={mi} value={mi}>{mi} mi</option>
                              ))}
                            </select>
                          </div>
                          <div className="mfd-block">
                            <label className="mfd-field-label">Dealer Type</label>
                            <div className="mfd-dealer-segmented">
                              <button type="button" className={`mfd-segmented-btn ${!activeCompsIndependentOnly ? "active" : ""}`} onClick={() => setActiveCompsIndependentOnly(false)}>All</button>
                              <button type="button" className={`mfd-segmented-btn ${activeCompsIndependentOnly ? "active" : ""}`} onClick={() => setActiveCompsIndependentOnly(true)}>Independent</button>
                            </div>
                          </div>
                        </section>
                        <div className="mfd-divider" />
                        <section className="mfd-section">
                          <h3 className="mfd-section-title">MATCHING</h3>
                          <div className="mfd-block">
                            <label className="mfd-field-label">Mileage</label>
                            <div className="mfd-range-text">{formatMileageRange(activeCompsMileageRange)}</div>
                            <div className="mfd-slider-container" style={{ "--min-val": activeCompsMileageRange[0], "--max-val": activeCompsMileageRange[1], "--min-bound": Math.min(activeCompsMileageBounds.min, activeCompsMileageRange[0]), "--max-bound": Math.max(activeCompsMileageBounds.max, activeCompsMileageRange[1]) }}>
                              <div className="mfd-slider-track">
                                <input type="range" min={Math.min(activeCompsMileageBounds.min, activeCompsMileageRange[0])} max={Math.max(activeCompsMileageBounds.max, activeCompsMileageRange[1])} value={activeCompsMileageRange[0]} onChange={(e) => { const newMin = parseInt(e.target.value, 10); setActiveCompsMileageRange([newMin, Math.max(newMin, activeCompsMileageRange[1])]); if (activeCompsSimilarMileage) setActiveCompsSimilarMileage(false); }} className="mfd-slider mfd-slider-min" disabled={activeCompsSimilarMileage} />
                                <input type="range" min={Math.min(activeCompsMileageBounds.min, activeCompsMileageRange[0])} max={Math.max(activeCompsMileageBounds.max, activeCompsMileageRange[1])} value={activeCompsMileageRange[1]} onChange={(e) => { const newMax = parseInt(e.target.value, 10); setActiveCompsMileageRange([Math.min(newMax, activeCompsMileageRange[0]), newMax]); if (activeCompsSimilarMileage) setActiveCompsSimilarMileage(false); }} className="mfd-slider mfd-slider-max" disabled={activeCompsSimilarMileage} />
                              </div>
                            </div>
                          </div>
                          <div className="mfd-block mfd-toggle-row">
                            <span className="mfd-toggle-label">Match Similar Mileage</span>
                            <label className="mfd-switch">
                              <input type="checkbox" checked={!!activeCompsSimilarMileage} onChange={() => { const newSimilar = !activeCompsSimilarMileage; setActiveCompsSimilarMileage(newSimilar); if (newSimilar && signal?.mileage) { const pct = activeCompsSimilarMileagePct / 100; setActiveCompsMileageRange([Math.floor(signal.mileage * (1 - pct)), Math.ceil(signal.mileage * (1 + pct))]); } else { setActiveCompsMileageRange([activeCompsMileageBounds.min, activeCompsMileageBounds.max]); } }} />
                              <span className="mfd-switch-slider" />
                            </label>
                          </div>
                          <div className="mfd-tolerance-row">
                            <span className="mfd-field-label">Tolerance</span>
                            <select className="mfd-select" value={activeCompsSimilarMileagePct} onChange={(e) => { const pct = Number(e.target.value); setActiveCompsSimilarMileagePct(pct); if (activeCompsSimilarMileage && signal?.mileage) { setActiveCompsMileageRange([Math.floor(signal.mileage * (1 - pct / 100)), Math.ceil(signal.mileage * (1 + pct / 100))]); } }} aria-label="Mileage variance percent">
                              {MILEAGE_VARIANCES.map((p) => (<option key={p} value={p}>±{p}%</option>))}
                            </select>
                          </div>
                          <div className="mfd-block mfd-toggle-row">
                            <span className="mfd-toggle-label">Match Version</span>
                            <label className="mfd-switch">
                              <input type="checkbox" checked={!!activeCompsMatchingVersion} onChange={() => setActiveCompsMatchingVersion(!activeCompsMatchingVersion)} />
                              <span className="mfd-switch-slider" />
                            </label>
                          </div>
                        </section>
                        <div className="mfd-divider" />
                      </div>
                      <footer className="mfd-footer">
                        <button type="button" className="mfd-apply" onClick={() => setMobileFiltersOpen(false)}>
                          <SearchOutlined />
                          View {activeCompsSummaryStats.activeListings} Listings
                        </button>
                      </footer>
                    </div>
                  </div>,
                  document.body
                )}
                <div className="act-mobile-summary-card">
                  <div className="act-mobile-summary-price">${activeCompsSummaryStats.avgPrice.toLocaleString()}</div>
                  <div className="act-mobile-summary-left">
                    <div className="act-mobile-summary-title">ACTIVE LISTINGS</div>
                    <div className="act-mobile-summary-count">{activeCompsSummaryStats.activeListings} Listings in Radius</div>
                  </div>
                </div>
                <div className="act-mobile-comps-list">
                  {activeComps.length > 0 ? (
                    activeComps.map((comp) => (
                      <CompRowCard
                        key={comp.id}
                        comp={{
                          id: comp.id,
                          image: comp.image || signal?.thumbnailUrl,
                          title: comp.heading,
                          price: comp.price,
                          mileage: comp.mileage,
                          dealer: comp.dealer,
                          url: comp.url,
                          dom: comp.dom,
                          version: comp.version,
                          transmission: null,
                          drivetrain: null,
                        }}
                        isSelected={false}
                        onClick={() => comp.url && window.open(comp.url, "_blank")}
                        isMobile={true}
                        compactHorizontal={true}
                      />
                    ))
                  ) : (
                    <div className="no-comps-message">No comparable vehicles found matching your criteria.</div>
                  )}
                </div>
              </div>
            </>
          ) : activeTab === "active" ? (
            <>
              {/* Market Control Bar */}
              <div className="vid-market-control-bar">
                {/* Row 1: Sort Controls */}
                <div className="vid-mcb-row-1">
                  <div className="vid-mcb-sort-group">
                    <span className="vid-mcb-label">Sort by</span>
                    <select
                      className="vid-mcb-select"
                      value={activeCompsSortBy}
                      onChange={(e) => setActiveCompsSortBy(e.target.value)}
                    >
                      <option value="price">Price</option>
                      <option value="mileage">Mileage</option>
                    </select>
                    <button 
                      className="vid-mcb-sort-toggle" 
                      onClick={() => setActiveCompsSortOrder(activeCompsSortOrder === "asc" ? "desc" : "asc")}
                    >
                      {activeCompsSortOrder === "asc" ? "↑" : "↓"} {activeCompsSortOrder === "asc" ? "Asc" : "Desc"}
                    </button>
                  </div>
                  <div className="vid-mcb-row-1-right">
                    <div className="vid-mcb-radius-wrap">
                      <span className="vid-mcb-label">Radius:</span>
                      <select
                        className="vid-mcb-select vid-mcb-radius-select"
                        value={activeCompsRadius}
                        onChange={(e) => setActiveCompsRadius(Number(e.target.value))}
                        aria-label="Search radius in miles"
                      >
                        {RADIUS_OPTIONS_ACTIVE.map((mi) => (
                          <option key={mi} value={mi}>{mi} mi</option>
                        ))}
                      </select>
                    </div>
                    <button 
                      className="vid-mcb-reset" 
                      onClick={() => {
                      setActiveCompsSortBy("price");
                      setActiveCompsSortOrder("asc");
                      setActiveCompsMileageRange([activeCompsMileageBounds.min, activeCompsMileageBounds.max]);
                      setActiveCompsSimilarMileage(false);
                      setActiveCompsIndependentOnly(false);
                      setActiveCompsMatchingVersion(false);
                      setActiveCompsRadius(100);
                    }}
                    >
                      Reset
                    </button>
                  </div>
                </div>

                {/* Row 2: Mileage Slider */}
                <div className="vid-mcb-row-2">
                  <span className="vid-mcb-label">Mileage</span>
                  <div 
                    className="vid-mcb-slider-container"
                    style={{
                      '--min-val': activeCompsMileageRange[0],
                      '--max-val': activeCompsMileageRange[1],
                      '--min-bound': Math.min(activeCompsMileageBounds.min, activeCompsMileageRange[0]),
                      '--max-bound': Math.max(activeCompsMileageBounds.max, activeCompsMileageRange[1])
                    }}
                  >
                    <div className="vid-mcb-slider-track">
                      <input
                        type="range"
                        min={Math.min(activeCompsMileageBounds.min, activeCompsMileageRange[0])}
                        max={Math.max(activeCompsMileageBounds.max, activeCompsMileageRange[1])}
                        value={activeCompsMileageRange[0]}
                        onChange={(e) => {
                          const newMin = parseInt(e.target.value);
                          setActiveCompsMileageRange([newMin, Math.max(newMin, activeCompsMileageRange[1])]);
                          if (activeCompsSimilarMileage) {
                            setActiveCompsSimilarMileage(false);
                          }
                        }}
                        className="vid-mcb-slider vid-mcb-slider-min"
                        disabled={activeCompsSimilarMileage}
                      />
                      <input
                        type="range"
                        min={Math.min(activeCompsMileageBounds.min, activeCompsMileageRange[0])}
                        max={Math.max(activeCompsMileageBounds.max, activeCompsMileageRange[1])}
                        value={activeCompsMileageRange[1]}
                        onChange={(e) => {
                          const newMax = parseInt(e.target.value);
                          setActiveCompsMileageRange([Math.min(newMax, activeCompsMileageRange[0]), newMax]);
                          if (activeCompsSimilarMileage) {
                            setActiveCompsSimilarMileage(false);
                          }
                        }}
                        className="vid-mcb-slider vid-mcb-slider-max"
                        disabled={activeCompsSimilarMileage}
                      />
                    </div>
                  </div>
                  <div className="vid-mcb-range-text">
                    {activeCompsMileageRange[0].toLocaleString()} – {activeCompsMileageRange[1].toLocaleString()} mi
                  </div>
                </div>

                {/* Row 3: Filter Chips */}
                <div className="vid-mcb-row-3">
                  <div
                    className={`vid-mcb-chip vid-mcb-chip-similar-mileage ${activeCompsSimilarMileage ? "active" : ""}`}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                  >
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        const newSimilar = !activeCompsSimilarMileage;
                        setActiveCompsSimilarMileage(newSimilar);
                        if (newSimilar && signal?.mileage) {
                          const pct = activeCompsSimilarMileagePct / 100;
                          const minMileage = Math.floor(signal.mileage * (1 - pct));
                          const maxMileage = Math.ceil(signal.mileage * (1 + pct));
                          setActiveCompsMileageRange([minMileage, maxMileage]);
                        } else {
                          setActiveCompsMileageRange([activeCompsMileageBounds.min, activeCompsMileageBounds.max]);
                        }
                      }}
                      onKeyDown={(e) => e.key === "Enter" && (e.currentTarget.click(), e.preventDefault())}
                      style={{ cursor: "pointer" }}
                    >
                      {activeCompsSimilarMileage && <span className="vid-mcb-chip-check">✓</span>}
                      Use similar mileage
                    </span>
                    <select
                      className="vid-mcb-mileage-pct-select"
                      value={activeCompsSimilarMileagePct}
                      onChange={(e) => {
                        const pct = Number(e.target.value);
                        setActiveCompsSimilarMileagePct(pct);
                        if (activeCompsSimilarMileage && signal?.mileage) {
                          const minMileage = Math.floor(signal.mileage * (1 - pct / 100));
                          const maxMileage = Math.ceil(signal.mileage * (1 + pct / 100));
                          setActiveCompsMileageRange([minMileage, maxMileage]);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Mileage variance percent"
                    >
                      {MILEAGE_VARIANCES.map((p) => (
                        <option key={p} value={p}>±{p}%</option>
                      ))}
                    </select>
                  </div>
                  <button
                    className={`vid-mcb-chip ${activeCompsIndependentOnly ? "active" : ""}`}
                    onClick={() => setActiveCompsIndependentOnly(!activeCompsIndependentOnly)}
                  >
                    {activeCompsIndependentOnly && <span className="vid-mcb-chip-check">✓</span>}
                    Independent Dealers Only
                  </button>
                  <button
                    className={`vid-mcb-chip ${activeCompsMatchingVersion ? "active" : ""}`}
                    onClick={() => setActiveCompsMatchingVersion(!activeCompsMatchingVersion)}
                  >
                    {activeCompsMatchingVersion && <span className="vid-mcb-chip-check">✓</span>}
                    Show Matching Version Only
                  </button>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="vid-summary-stats-row">
                <div className="vid-ssr-card">
                  <div className="vid-ssr-content">
                    <div className="vid-ssr-label">Active Listings</div>
                    <div className="vid-ssr-value">{activeCompsSummaryStats.activeListings}</div>
                  </div>
                </div>
                <div className="vid-ssr-card vid-ssr-card-hero">
                  <div className="vid-ssr-content">
                    <div className="vid-ssr-label">Average Price</div>
                    <div className="vid-ssr-value">${activeCompsSummaryStats.avgPrice.toLocaleString()}</div>
                  </div>
                </div>
                <div className="vid-ssr-card">
                  <div className="vid-ssr-content">
                    <div className="vid-ssr-label">Average Mileage</div>
                    <div className="vid-ssr-value">{activeCompsSummaryStats.avgMileage.toLocaleString()} mi</div>
                  </div>
                </div>
              </div>

              {/* Active Comps Cards */}
              <div className="vid-comps-card">
                {activeComps.length > 0 ? (
                  activeComps.map((comp) => (
                    <div key={comp.id} className="vid-comp-row-card" onClick={() => comp.url && window.open(comp.url, '_blank')}>
                      <div className="vid-crc-thumbnail">
                        <ImageThumbnail imageUrl={comp.image || signal?.thumbnailUrl} alt={comp.heading} />
                      </div>
                      <div className="vid-crc-title-block">
                        <div className="vid-crc-title">
                          {comp.heading}
                          {comp.version && <span className="vid-crc-title-version"> • {comp.version}</span>}
                        </div>
                        <div className="vid-crc-price-row">
                          <span className="vid-crc-price">{(comp.price != null && comp.price > 0) ? `$${comp.price.toLocaleString()}` : "—"}</span>
                          <span className="vid-crc-mileage">{comp.mileage.toLocaleString()} mi</span>
                        </div>
                        <div className="vid-crc-dealer-row">
                          <span className="vid-crc-dealer">{comp.dealer}</span>
                        </div>
                      </div>
                      <div className="vid-crc-right-cell">
                        {(comp.dom != null || comp.distance_miles != null) && (
                          <div className="vid-crc-right-info">
                            {comp.dom != null && <div className="vid-crc-dom">DOM: {comp.dom} days</div>}
                            {comp.distance_miles != null && (
                              <div className="vid-crc-distance">
                                {Number(comp.distance_miles) === Math.round(comp.distance_miles)
                                  ? `${comp.distance_miles} mi`
                                  : `${Number(comp.distance_miles).toFixed(1)} mi`}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="vid-crc-chevron">
                          <span>›</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="vid-no-comps-message">No comparable vehicles found matching your criteria.</div>
                )}
              </div>
            </>
          ) : null}

          {activeTab === "sold" && isMobile ? (
            <>
              <div className="active-comps-tab active-comps-tab-mobile">
                <div className="act-mobile-pills">
                  <button type="button" className="act-mobile-pill act-mobile-pill-filters" onClick={() => setMobileSoldFiltersOpen(true)}>
                    Filters {[soldCompsSimilarMileage, soldCompsIndependentOnly, soldCompsMatchingVersion].filter(Boolean).length > 0 ? `(${[soldCompsSimilarMileage, soldCompsIndependentOnly, soldCompsMatchingVersion].filter(Boolean).length})` : ""}
                  </button>
                  <button type="button" className="act-mobile-pill act-mobile-pill-sort" onClick={() => setSoldCompsSortOrder(soldCompsSortOrder === "asc" ? "desc" : "asc")}>
                    Sort: {soldCompsSortBy === "soldDate" ? "Sold Date" : soldCompsSortBy === "price" ? "Price" : "Mileage"} {soldCompsSortOrder === "asc" ? "↑" : "↓"}
                  </button>
                </div>
                {mobileSoldFiltersOpen && ReactDOM.createPortal(
                  <div className="mfd-overlay" onClick={() => setMobileSoldFiltersOpen(false)}>
                    <div className="mfd-drawer" onClick={(e) => e.stopPropagation()}>
                      <header className="mfd-header">
                        <button type="button" className="mfd-close" onClick={() => setMobileSoldFiltersOpen(false)} aria-label="Close">
                          <CloseOutlined />
                        </button>
                        <h2 className="mfd-title">Filters</h2>
                        <button type="button" className="mfd-clear" onClick={() => { setSoldCompsSortBy("soldDate"); setSoldCompsSortOrder("desc"); setSoldCompsMileageRange([soldCompsMileageBounds.min, soldCompsMileageBounds.max]); setSoldCompsSimilarMileage(false); setSoldCompsIndependentOnly(false); setSoldCompsMatchingVersion(false); setSoldCompsRadius(100); }}>
                          Clear
                        </button>
                      </header>
                      <div className="mfd-body">
                        <div className="mfd-divider" />
                        <section className="mfd-section">
                          <h3 className="mfd-section-title">MARKET SCOPE</h3>
                          <div className="mfd-block">
                            <label className="mfd-field-label">Radius</label>
                            <select
                              className="mfd-select"
                              value={soldCompsRadius}
                              onChange={(e) => setSoldCompsRadius(Number(e.target.value))}
                              aria-label="Search radius in miles"
                            >
                              {RADIUS_OPTIONS_SOLD.map((mi) => (
                                <option key={mi} value={mi}>{mi} mi</option>
                              ))}
                            </select>
                          </div>
                          <div className="mfd-block">
                            <label className="mfd-field-label">Dealer Type</label>
                            <div className="mfd-dealer-segmented">
                              <button type="button" className={`mfd-segmented-btn ${!soldCompsIndependentOnly ? "active" : ""}`} onClick={() => setSoldCompsIndependentOnly(false)}>All</button>
                              <button type="button" className={`mfd-segmented-btn ${soldCompsIndependentOnly ? "active" : ""}`} onClick={() => setSoldCompsIndependentOnly(true)}>Independent</button>
                            </div>
                          </div>
                        </section>
                        <div className="mfd-divider" />
                        <section className="mfd-section">
                          <h3 className="mfd-section-title">MATCHING</h3>
                          <div className="mfd-block">
                            <label className="mfd-field-label">Mileage</label>
                            <div className="mfd-range-text">{formatMileageRange(soldCompsMileageRange)}</div>
                            <div className="mfd-slider-container" style={{ "--min-val": soldCompsMileageRange[0], "--max-val": soldCompsMileageRange[1], "--min-bound": Math.min(soldCompsMileageBounds.min, soldCompsMileageRange[0]), "--max-bound": Math.max(soldCompsMileageBounds.max, soldCompsMileageRange[1]) }}>
                              <div className="mfd-slider-track">
                                <input type="range" min={Math.min(soldCompsMileageBounds.min, soldCompsMileageRange[0])} max={Math.max(soldCompsMileageBounds.max, soldCompsMileageRange[1])} value={soldCompsMileageRange[0]} onChange={(e) => { const newMin = parseInt(e.target.value, 10); setSoldCompsMileageRange([newMin, Math.max(newMin, soldCompsMileageRange[1])]); if (soldCompsSimilarMileage) setSoldCompsSimilarMileage(false); }} className="mfd-slider mfd-slider-min" disabled={soldCompsSimilarMileage} />
                                <input type="range" min={Math.min(soldCompsMileageBounds.min, soldCompsMileageRange[0])} max={Math.max(soldCompsMileageBounds.max, soldCompsMileageRange[1])} value={soldCompsMileageRange[1]} onChange={(e) => { const newMax = parseInt(e.target.value, 10); setSoldCompsMileageRange([Math.min(newMax, soldCompsMileageRange[0]), newMax]); if (soldCompsSimilarMileage) setSoldCompsSimilarMileage(false); }} className="mfd-slider mfd-slider-max" disabled={soldCompsSimilarMileage} />
                              </div>
                            </div>
                          </div>
                          <div className="mfd-block mfd-toggle-row">
                            <span className="mfd-toggle-label">Match Similar Mileage</span>
                            <label className="mfd-switch">
                              <input type="checkbox" checked={!!soldCompsSimilarMileage} onChange={() => { const newSimilar = !soldCompsSimilarMileage; setSoldCompsSimilarMileage(newSimilar); if (newSimilar && signal?.mileage) { const pct = soldCompsSimilarMileagePct / 100; setSoldCompsMileageRange([Math.floor(signal.mileage * (1 - pct)), Math.ceil(signal.mileage * (1 + pct))]); } else { setSoldCompsMileageRange([soldCompsMileageBounds.min, soldCompsMileageBounds.max]); } }} />
                              <span className="mfd-switch-slider" />
                            </label>
                          </div>
                          <div className="mfd-tolerance-row">
                            <span className="mfd-field-label">Tolerance</span>
                            <select className="mfd-select" value={soldCompsSimilarMileagePct} onChange={(e) => { const pct = Number(e.target.value); setSoldCompsSimilarMileagePct(pct); if (soldCompsSimilarMileage && signal?.mileage) { setSoldCompsMileageRange([Math.floor(signal.mileage * (1 - pct / 100)), Math.ceil(signal.mileage * (1 + pct / 100))]); } }} aria-label="Mileage variance percent">
                              {MILEAGE_VARIANCES.map((p) => (<option key={p} value={p}>±{p}%</option>))}
                            </select>
                          </div>
                          <div className="mfd-block mfd-toggle-row">
                            <span className="mfd-toggle-label">Match Version</span>
                            <label className="mfd-switch">
                              <input type="checkbox" checked={!!soldCompsMatchingVersion} onChange={() => setSoldCompsMatchingVersion(!soldCompsMatchingVersion)} />
                              <span className="mfd-switch-slider" />
                            </label>
                          </div>
                        </section>
                        <div className="mfd-divider" />
                      </div>
                      <footer className="mfd-footer">
                        <button type="button" className="mfd-apply" onClick={() => setMobileSoldFiltersOpen(false)}>
                          <SearchOutlined />
                          View {soldCompsSummaryStats.soldListings} Listings
                        </button>
                      </footer>
                    </div>
                  </div>,
                  document.body
                )}
                <div className="act-mobile-summary-card">
                  <div className="act-mobile-summary-price">${soldCompsSummaryStats.avgPrice.toLocaleString()}</div>
                  <div className="act-mobile-summary-left">
                    <div className="act-mobile-summary-title">SOLD LISTINGS</div>
                    <div className="act-mobile-summary-count">{soldCompsSummaryStats.soldListings} Listings in Radius</div>
                  </div>
                </div>
                <div className="act-mobile-comps-list">
                  {soldComps.length > 0 ? (
                    soldComps.map((comp) => {
                      const formatSoldDate = (dateString) => {
                        if (!dateString) return "";
                        try {
                          const date = new Date(dateString);
                          return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                        } catch (e) {
                          return dateString;
                        }
                      };
                      return (
                        <CompRowCard
                          key={comp.id}
                          comp={{
                            id: comp.id,
                            image: comp.image || signal?.thumbnailUrl,
                            title: comp.heading,
                            price: comp.price,
                            mileage: comp.mileage,
                            dealer: comp.dealer,
                            url: comp.url,
                            dom: comp.dom,
                            version: comp.version,
                            soldDate: comp.soldDate || comp.lastSeenAtDate,
                            transmission: null,
                            drivetrain: null,
                          }}
                          isSelected={false}
                          onClick={() => {}}
                          isMobile={true}
                          compactHorizontal={true}
                        />
                      );
                    })
                  ) : (
                    <div className="no-comps-message">No comparable vehicles found matching your criteria.</div>
                  )}
                </div>
              </div>
            </>
          ) : activeTab === "sold" ? (
            <>
              {/* Market Control Bar */}
              <div className="vid-market-control-bar">
                {/* Row 1: Sort Controls */}
                <div className="vid-mcb-row-1">
                  <div className="vid-mcb-sort-group">
                    <span className="vid-mcb-label">Sort by</span>
                    <select
                      className="vid-mcb-select"
                      value={soldCompsSortBy}
                      onChange={(e) => setSoldCompsSortBy(e.target.value)}
                    >
                      <option value="soldDate">Sold Date</option>
                      <option value="price">Price</option>
                      <option value="mileage">Mileage</option>
                    </select>
                    <button 
                      className="vid-mcb-sort-toggle" 
                      onClick={() => setSoldCompsSortOrder(soldCompsSortOrder === "asc" ? "desc" : "asc")}
                    >
                      {soldCompsSortOrder === "asc" ? "↑" : "↓"} {soldCompsSortOrder === "asc" ? "Asc" : "Desc"}
                    </button>
                  </div>
                  <div className="vid-mcb-row-1-right">
                    <div className="vid-mcb-radius-wrap">
                      <span className="vid-mcb-label">Radius:</span>
                      <select
                        className="vid-mcb-select vid-mcb-radius-select"
                        value={soldCompsRadius}
                        onChange={(e) => setSoldCompsRadius(Number(e.target.value))}
                        aria-label="Search radius in miles"
                      >
                        {RADIUS_OPTIONS_SOLD.map((mi) => (
                          <option key={mi} value={mi}>{mi} mi</option>
                        ))}
                      </select>
                    </div>
                    <button 
                      className="vid-mcb-reset" 
                      onClick={() => {
                        setSoldCompsSortBy("soldDate");
                      setSoldCompsSortOrder("desc");
                      setSoldCompsMileageRange([soldCompsMileageBounds.min, soldCompsMileageBounds.max]);
                      setSoldCompsSimilarMileage(false);
                      setSoldCompsIndependentOnly(false);
                      setSoldCompsMatchingVersion(false);
                      setSoldCompsRadius(100);
                    }}
                    >
                      Reset
                    </button>
                  </div>
                </div>

                {/* Row 2: Mileage Slider */}
                <div className="vid-mcb-row-2">
                  <span className="vid-mcb-label">Mileage</span>
                  <div 
                    className="vid-mcb-slider-container"
                    style={{
                      '--min-val': soldCompsMileageRange[0],
                      '--max-val': soldCompsMileageRange[1],
                      '--min-bound': Math.min(soldCompsMileageBounds.min, soldCompsMileageRange[0]),
                      '--max-bound': Math.max(soldCompsMileageBounds.max, soldCompsMileageRange[1])
                    }}
                  >
                    <div className="vid-mcb-slider-track">
                      <input
                        type="range"
                        min={Math.min(soldCompsMileageBounds.min, soldCompsMileageRange[0])}
                        max={Math.max(soldCompsMileageBounds.max, soldCompsMileageRange[1])}
                        value={soldCompsMileageRange[0]}
                        onChange={(e) => {
                          const newMin = parseInt(e.target.value);
                          setSoldCompsMileageRange([newMin, Math.max(newMin, soldCompsMileageRange[1])]);
                          if (soldCompsSimilarMileage) {
                            setSoldCompsSimilarMileage(false);
                          }
                        }}
                        className="vid-mcb-slider vid-mcb-slider-min"
                        disabled={soldCompsSimilarMileage}
                      />
                      <input
                        type="range"
                        min={Math.min(soldCompsMileageBounds.min, soldCompsMileageRange[0])}
                        max={Math.max(soldCompsMileageBounds.max, soldCompsMileageRange[1])}
                        value={soldCompsMileageRange[1]}
                        onChange={(e) => {
                          const newMax = parseInt(e.target.value);
                          setSoldCompsMileageRange([Math.min(newMax, soldCompsMileageRange[0]), newMax]);
                          if (soldCompsSimilarMileage) {
                            setSoldCompsSimilarMileage(false);
                          }
                        }}
                        className="vid-mcb-slider vid-mcb-slider-max"
                        disabled={soldCompsSimilarMileage}
                      />
                    </div>
                  </div>
                  <div className="vid-mcb-range-text">
                    {soldCompsMileageRange[0].toLocaleString()} – {soldCompsMileageRange[1].toLocaleString()} mi
                  </div>
                </div>

                {/* Row 3: Filter Chips */}
                <div className="vid-mcb-row-3">
                  <div
                    className={`vid-mcb-chip vid-mcb-chip-similar-mileage ${soldCompsSimilarMileage ? "active" : ""}`}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                  >
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        const newSimilar = !soldCompsSimilarMileage;
                        setSoldCompsSimilarMileage(newSimilar);
                        if (newSimilar && signal?.mileage) {
                          const pct = soldCompsSimilarMileagePct / 100;
                          const minMileage = Math.floor(signal.mileage * (1 - pct));
                          const maxMileage = Math.ceil(signal.mileage * (1 + pct));
                          setSoldCompsMileageRange([minMileage, maxMileage]);
                        } else {
                          setSoldCompsMileageRange([soldCompsMileageBounds.min, soldCompsMileageBounds.max]);
                        }
                      }}
                      onKeyDown={(e) => e.key === "Enter" && (e.currentTarget.click(), e.preventDefault())}
                      style={{ cursor: "pointer" }}
                    >
                      {soldCompsSimilarMileage && <span className="vid-mcb-chip-check">✓</span>}
                      Use similar mileage
                    </span>
                    <select
                      className="vid-mcb-mileage-pct-select"
                      value={soldCompsSimilarMileagePct}
                      onChange={(e) => {
                        const pct = Number(e.target.value);
                        setSoldCompsSimilarMileagePct(pct);
                        if (soldCompsSimilarMileage && signal?.mileage) {
                          const minMileage = Math.floor(signal.mileage * (1 - pct / 100));
                          const maxMileage = Math.ceil(signal.mileage * (1 + pct / 100));
                          setSoldCompsMileageRange([minMileage, maxMileage]);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Mileage variance percent"
                    >
                      {MILEAGE_VARIANCES.map((p) => (
                        <option key={p} value={p}>±{p}%</option>
                      ))}
                    </select>
                  </div>
                  <button
                    className={`vid-mcb-chip ${soldCompsIndependentOnly ? "active" : ""}`}
                    onClick={() => setSoldCompsIndependentOnly(!soldCompsIndependentOnly)}
                  >
                    {soldCompsIndependentOnly && <span className="vid-mcb-chip-check">✓</span>}
                    Independent Dealers Only
                  </button>
                  <button
                    className={`vid-mcb-chip ${soldCompsMatchingVersion ? "active" : ""}`}
                    onClick={() => setSoldCompsMatchingVersion(!soldCompsMatchingVersion)}
                  >
                    {soldCompsMatchingVersion && <span className="vid-mcb-chip-check">✓</span>}
                    Show Matching Version Only
                  </button>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="vid-summary-stats-row vid-summary-stats-row-4">
                <div className="vid-ssr-card">
                  <div className="vid-ssr-content">
                    <div className="vid-ssr-label">Sold Listings</div>
                    <div className="vid-ssr-value">{soldCompsSummaryStats.soldListings}</div>
                  </div>
                </div>
                <div className="vid-ssr-card vid-ssr-card-hero">
                  <div className="vid-ssr-content">
                    <div className="vid-ssr-label">Average Price</div>
                    <div className="vid-ssr-value">${soldCompsSummaryStats.avgPrice.toLocaleString()}</div>
                  </div>
                </div>
                <div className="vid-ssr-card">
                  <div className="vid-ssr-content">
                    <div className="vid-ssr-label">Average Mileage</div>
                    <div className="vid-ssr-value">{soldCompsSummaryStats.avgMileage.toLocaleString()} mi</div>
                  </div>
                </div>
                <div className={`vid-ssr-card ${soldCompsSummaryStats.avgSoldDom < 45 ? 'vid-ssr-card-green' : soldCompsSummaryStats.avgSoldDom <= 90 ? 'vid-ssr-card-yellow' : 'vid-ssr-card-red'}`}>
                  <div className="vid-ssr-content">
                    <div className="vid-ssr-label">Sold DOM</div>
                    <div className={`vid-ssr-value ${soldCompsSummaryStats.avgSoldDom < 45 ? 'vid-ssr-value-green' : soldCompsSummaryStats.avgSoldDom <= 90 ? 'vid-ssr-value-yellow' : 'vid-ssr-value-red'}`}>
                      {soldCompsSummaryStats.avgSoldDom} days
                    </div>
                  </div>
                </div>
              </div>

              {/* Sold Comps Cards */}
              <div className="vid-comps-card">
                {soldComps.length > 0 ? (
                  soldComps.map((comp) => {
                    const formatSoldDate = (dateString) => {
                      if (!dateString) return '';
                      try {
                        const date = new Date(dateString);
                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                      } catch (e) {
                        return dateString;
                      }
                    };
                    return (
                      <div key={comp.id} className="vid-comp-row-card vid-comp-row-card-sold">
                        <div className="vid-crc-thumbnail">
                          <ImageThumbnail imageUrl={comp.image || signal?.thumbnailUrl} alt={comp.heading} />
                        </div>
                        <div className="vid-crc-title-block">
                          <div className="vid-crc-title">
                            {comp.heading}
                            {comp.version && <span className="vid-crc-title-version"> • {comp.version}</span>}
                          </div>
                          <div className="vid-crc-price-row">
                            <span className="vid-crc-price">{(comp.price != null && comp.price > 0) ? `$${comp.price.toLocaleString()}` : "—"}</span>
                            <span className="vid-crc-mileage">{comp.mileage.toLocaleString()} mi</span>
                          </div>
                          <div className="vid-crc-dealer-row">
                            <span className="vid-crc-dealer">{comp.dealer}</span>
                          </div>
                        </div>
                        <div className="vid-crc-right-cell">
                          {(comp.dom != null || comp.distance_miles != null || comp.soldDate) && (
                            <div className="vid-crc-right-info">
                              {comp.dom != null && <div className="vid-crc-dom">DOM: {comp.dom} days</div>}
                              {comp.distance_miles != null && (
                                <div className="vid-crc-distance">
                                  {Number(comp.distance_miles) === Math.round(comp.distance_miles)
                                    ? `${comp.distance_miles} mi`
                                    : `${Number(comp.distance_miles).toFixed(1)} mi`}
                                </div>
                              )}
                              {comp.soldDate && (
                                <div className="vid-crc-sold-date">Sold Date: {formatSoldDate(comp.soldDate)}</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="vid-no-comps-message">No comparable vehicles found matching your criteria.</div>
                )}
              </div>
            </>
          ) : null}
        </div>

        {/* Footer Actions */}
        <div className="vid-footer">
          <Button 
            className="vid-footer-btn vid-view-listing-btn" 
            onClick={() => signal?.vdpUrl && window.open(signal.vdpUrl, '_blank')}
            block
          >
            View Listing
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
